// Seq logging transport. POSTs events to a local (or env-configured) Seq
// instance using the CLEF (Compact Log Event Format) endpoint. Failures are
// swallowed — logging never throws.
//
// `patchConsole({ appName })` redirects `console.*` through this transport
// when NODE_ENV === 'development'.

import axios from 'axios';

const DEFAULT_SEQ_URL = 'http://localhost:5455';
const DEV_NODE_ENV = 'development';

export enum LogLevel {
  Verbose = 'Verbose',
  Debug = 'Debug',
  Info = 'Information',
  Warning = 'Warning',
  Error = 'Error',
  Fatal = 'Fatal',
}

export type LogProps = Record<string, unknown>;

interface SeqRuntimeConfig {
  appName: string;
  layer: string;
  seqUrl: string;
  enabled: boolean;
}

function readEnv(name: string): string | undefined {
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  return env?.[name];
}

function isDevEnv(): boolean {
  return readEnv('NODE_ENV') === DEV_NODE_ENV;
}

// Defaults eagerly evaluated at module load. In dev, seqLog is on by default
// — persistent-log callers and direct seqLog users don't need to configure.
// In other envs it stays off unless the app calls configureSeq({ enabled: true }).
const config: SeqRuntimeConfig = {
  appName: 'cabal-unknown',
  layer: 'backend',
  seqUrl: readEnv('SEQ_URL') ?? DEFAULT_SEQ_URL,
  enabled: isDevEnv(),
};

export interface ConfigureSeqInput {
  appName: string;
  layer?: 'backend' | 'frontend' | string;
  seqUrl?: string;
  enabled?: boolean;
}

export function configureSeq(input: ConfigureSeqInput): void {
  config.appName = input.appName;
  if (input.layer) config.layer = input.layer;
  if (input.seqUrl) config.seqUrl = input.seqUrl;
  if (input.enabled !== undefined) config.enabled = input.enabled;
}

function resolveSeqUrl(override?: string): string {
  if (override) return override;
  return readEnv('SEQ_URL') ?? DEFAULT_SEQ_URL;
}

export async function seqLog(
  message: string,
  propsOrLevel?: LogProps | LogLevel,
  maybeLevel?: LogLevel,
): Promise<void> {
  if (!config.enabled) return;

  const rawProps = typeof propsOrLevel === 'object' ? propsOrLevel : undefined;
  const level = typeof propsOrLevel === 'string' ? propsOrLevel : (maybeLevel ?? LogLevel.Debug);
  const { $note, $hideWhen, ...props } = (rawProps ?? {}) as LogProps & {
    $note?: string;
    $hideWhen?: boolean;
  };
  if ($hideWhen) return;

  const event = {
    '@t': new Date().toISOString(),
    '@l': level,
    '@mt': message,
    app: config.appName,
    layer: config.layer,
    ...($note !== undefined && { note: $note }),
    ...props,
  };

  let body: string;
  try {
    body = JSON.stringify(event);
  } catch {
    body = JSON.stringify({
      '@t': event['@t'],
      '@l': LogLevel.Warning,
      '@mt': '[seq-log] failed to serialize event',
      app: config.appName,
      layer: config.layer,
      originalMessage: message,
    });
  }

  try {
    await axios.post(`${config.seqUrl}/api/events/raw`, body, {
      headers: { 'Content-Type': 'application/vnd.serilog.clef' },
    });
  } catch {
    // never throw from a logger
  }
}

interface OriginalConsoleMethods {
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
}

let consolePatched = false;
let originalConsole: OriginalConsoleMethods | null = null;

function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return String(value);
  if (value instanceof Error) return value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function buildEventFromConsoleArgs(args: unknown[]): { message: string; props: LogProps } {
  if (args.length === 0) return { message: '', props: {} };

  const [first, ...rest] = args;
  const message = safeStringify(first);
  const props: LogProps = {};

  if (first instanceof Error) {
    props.errorName = first.name;
    props.errorMessage = first.message;
    if (first.stack) props.stack = first.stack;
  }

  rest.forEach((arg, i) => {
    const key = `arg${i + 1}`;
    if (arg instanceof Error) {
      props[key] = {
        name: arg.name,
        message: arg.message,
        stack: arg.stack,
      };
    } else {
      props[key] = arg;
    }
  });

  return { message, props };
}

export interface PatchConsoleInput {
  appName: string;
  layer?: 'backend' | 'frontend' | string;
  seqUrl?: string;
  /** Defaults to `process.env.NODE_ENV === 'development'`. */
  enabled?: boolean;
}

/**
 * Patches global `console.*` so calls also flow to Seq.
 *
 * Idempotent — calling twice is a no-op. Only activates when `enabled` is true
 * (defaults to `NODE_ENV === 'development'`). The original console output is
 * preserved; Seq is added as an extra sink.
 */
export function patchConsole(input: PatchConsoleInput): void {
  const enabled = input.enabled ?? isDevEnv();

  configureSeq({
    appName: input.appName,
    layer: input.layer ?? config.layer,
    seqUrl: resolveSeqUrl(input.seqUrl),
    enabled,
  });

  if (!enabled || consolePatched) return;

  originalConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  };

  const make =
    (level: LogLevel, original: (...args: unknown[]) => void) =>
    (...args: unknown[]): void => {
      original(...args);
      try {
        const { message, props } = buildEventFromConsoleArgs(args);
        void seqLog(message, props, level);
      } catch {
        // never throw from console
      }
    };

  console.log = make(LogLevel.Info, originalConsole.log);
  console.info = make(LogLevel.Info, originalConsole.info);
  console.warn = make(LogLevel.Warning, originalConsole.warn);
  console.error = make(LogLevel.Error, originalConsole.error);
  console.debug = make(LogLevel.Debug, originalConsole.debug);

  consolePatched = true;
}

/** For tests. Restores the original console methods. */
export function unpatchConsole(): void {
  if (!consolePatched || !originalConsole) return;
  console.log = originalConsole.log;
  console.info = originalConsole.info;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  console.debug = originalConsole.debug;
  originalConsole = null;
  consolePatched = false;
}
